import { z } from 'zod';
import { isoDateTime, memberRole, uuid } from './primitives';

/**
 * Auth + tenancy wire contracts (Master Spec §6.1, §14 auth). Promoted from
 * `apps/server/src/auth/schemas.ts` now that the web client (WP-10) consumes them.
 * The server keeps its own parse schemas; these are the single vocabulary the
 * client shares so register/login/session shapes agree by construction (§8.2).
 */

const email = z.string().trim().toLowerCase().email();
const password = z.string().min(8).max(200);

export const registerRequest = z.object({
  email,
  password,
  name: z.string().trim().min(1).max(200),
  workspaceName: z.string().trim().min(1).max(200),
  // #58: optional signup code (email/password path only). Enforced server-side when
  // `SIGNUP_CODE` is configured; Google SSO is exempt.
  signupCode: z.string().max(200).optional(),
});
export type RegisterRequest = z.infer<typeof registerRequest>;

export const loginRequest = z.object({ email, password });
export type LoginRequest = z.infer<typeof loginRequest>;

/**
 * #56 "Sign in with Google" (OIDC login) wire contracts. Distinct from the mailbox
 * connect flow (connections.ts): login requests only `openid email profile`, mints
 * a session, and never stores tokens.
 */

/** GET /api/auth/google/status — whether the Google login button should show. */
export const googleLoginStatus = z.object({ configured: z.boolean() });
export type GoogleLoginStatus = z.infer<typeof googleLoginStatus>;

/** POST /api/auth/google/start — returns the authorize URL + signed CSRF state. */
export const googleLoginStartResult = z.object({
  authorizeUrl: z.string().url(),
  state: z.string().min(1),
});
export type GoogleLoginStartResult = z.infer<typeof googleLoginStartResult>;

/** POST /api/auth/google/callback — Google redirects back with `code` + `state`. */
export const googleLoginCallback = z.object({
  code: z.string().min(1),
  state: z.string().min(1),
});
export type GoogleLoginCallback = z.infer<typeof googleLoginCallback>;

export const inviteRequest = z.object({ email, role: memberRole });
export type InviteRequest = z.infer<typeof inviteRequest>;

/** The authenticated user (never carries a password hash). */
export const authUser = z.object({
  id: uuid,
  email: z.string(),
  name: z.string(),
});
export type AuthUser = z.infer<typeof authUser>;

/** A workspace the user belongs to, with its display name and their role (from GET /auth/me). */
export const workspaceMembership = z.object({
  id: uuid,
  name: z.string(),
  role: memberRole,
});
export type WorkspaceMembership = z.infer<typeof workspaceMembership>;

/** POST /auth/register|login response. `workspaceId` is present on register. */
export const sessionResponse = z.object({
  token: z.string(),
  expiresAt: isoDateTime,
  user: authUser,
  workspaceId: uuid.optional(),
});
export type SessionResponse = z.infer<typeof sessionResponse>;

/** GET /auth/me response. */
export const meResponse = z.object({
  user: authUser,
  workspaces: z.array(workspaceMembership),
});
export type MeResponse = z.infer<typeof meResponse>;
